import { UrlObject } from 'url';

import type { NextRouter } from 'next/router';

import type { ReplaceOptionsParameters, Url } from './types';

export class NextRouterUrlHandler {
  private type: Url | object;

  private isAbsolute: boolean;

  private stringifiedUrl: string;

  private jsUrl: URL;

  constructor(private url: Url) {
    this.url = url;
    // @ts-ignore
    this.type = typeof url;
    this.stringifiedUrl = (typeof url === 'string') ? url : url.pathname || '';
    this.isAbsolute = this.stringifiedUrl.startsWith('http') || (url as string).startsWith('//');
    this.jsUrl = new URL(this.stringifiedUrl, window.location.origin);
  }

  getJsURL() {
    return this.jsUrl;
  }

  getStringifiedUrl() {
    return this.stringifiedUrl;
  }

  getPathname(asString: boolean = false) {
    if (!asString) {
      return this.url;
    }

    if (this.type === 'string') {
      return this.url;
    }

    return (this.url as UrlObject).pathname;
  }

  getUrlWithSearch(search: string) {
    const path = this.getPathWithSearch(search);

    if (this.type === 'string') {
      return path;
    }

    (this.url as UrlObject).pathname = path;

    return (this.url as UrlObject);
  }

  getPathWithSearch(search: string = '') {
    if (this.isAbsolute) {
      return `${this.jsUrl.origin}${this.jsUrl.pathname}${search}`;
    }

    return `${this.jsUrl.pathname}${search}`;
  }
}

export const getHistoryChangeMethod = (router: NextRouter) => (
  url: Url,
  as?: Url,
  options?: ReplaceOptionsParameters,
) => {
  const { keepQuery, ...originalOptions } = options || {};
  const urlHandler = new NextRouterUrlHandler(url);
  const stringifiedUrl = urlHandler.getStringifiedUrl();
  const isNotKeepSearchParameters = !keepQuery;
  const isUrlEqualsToCurrentPageUrl = !stringifiedUrl || stringifiedUrl === router.pathname;
  const currentSearchParams = new URLSearchParams(window.location.search);
  const isNoSearchParameterToAdd = Array.from(currentSearchParams).length === 0;

  if (isNotKeepSearchParameters || isUrlEqualsToCurrentPageUrl || isNoSearchParameterToAdd) {
    return router.replace(url, as, originalOptions);
  }

  const newUrl = urlHandler.getJsURL();
  currentSearchParams.forEach((value, key) => {
    newUrl.searchParams.append(key, value);
  });

  const urlSearch = newUrl.search;
  const urlPath = urlHandler.getUrlWithSearch(urlSearch);

  const isUrlForBrowserBarNotProvided = !as;

  if (isUrlForBrowserBarNotProvided) {
    const asPath = urlHandler.getPathWithSearch(currentSearchParams.toString());

    return router.replace(urlPath, asPath, originalOptions);
  }

  const asUrlHandler = new NextRouterUrlHandler(as!);
  const stringifiedAsUrl = asUrlHandler.getStringifiedUrl();

  if (!stringifiedAsUrl) {
    const asPath = urlHandler.getPathWithSearch(currentSearchParams.toString());

    return router.replace(urlPath, asPath, originalOptions);
  }

  const newAsUrl = asUrlHandler.getJsURL();
  currentSearchParams
    .forEach((value, key) => newAsUrl.searchParams.append(key, value));
  const asUrlSearch = newAsUrl.search;
  const asPath = urlHandler.getUrlWithSearch(asUrlSearch);

  return router.replace(urlPath, asPath, originalOptions);
};
